import type { AggregatedReportResponseSchema } from '@gto/api/vue-query'

type AvailableNodeEdits = Record<string, unknown>
type MergedActions = Record<string, unknown>

type FilterAction = {
  code: string
}
type OrderReverse = {
  frequency: number
}

type FlopSortByAction = {
  order_reverse: OrderReverse
}

type SortByIdx = string | number
type SortByValue = string | number
type SortByAction = string

export type SortKey = SortByIdx | SortByValue | SortByAction | 'data_idx'

export type SortAction = {
  order_reverse?: string | number
  group_display_name?: string
  actions?: { order_reverse?: string | number }[]
}

export interface AvailableActions {
  action: {
    code: string
    position: string
    type: string
    betsize: string
    allin: boolean
    is_hand_end: boolean
    is_showdown: boolean
    next_street: boolean
    display_name: string
    simple_group: string
    advanced_group: string
    betsize_by_pot: number | string | null // Allow both string and number
    next_position: string
  }
  frequency: number | null
  solved_action_count: number | null
  is_solution_end: boolean
  can_be_solved_by_ai: boolean
  next_position: string
  selected: boolean
}

type Game = Record<string, unknown>

export interface ActionSolution {
  action_code: string
  frequency: number | null
  solved_action_count: number | null
}

export interface PlayerSolution {
  position: string
  ev: number
  eq: number
  eqr: number
}

export interface Solution {
  name: string
  solutions: ActionSolution[]
  frequency?: number
}

export interface Player {
  position: string
  is_hero: boolean
  relative_position: string
}

export interface GamePoint {
  game: Game
  available_actions: AvailableActions[]
  custom_solution_id: string | null
  is_node_locked: boolean
  is_edited: boolean
  forced_fold: boolean
  available_node_edits: AvailableNodeEdits | null
  merged_actions: MergedActions[]
  preset_action_code: string
}

export type ColValue = {
  key: string
  val: string
  showProp?: string
}

export interface SortConf {
  sortBy: string
  sortByIdx?: string
  sortByValue?: string
  sortByAction?: {
    code: string
    order_reverse?: string
    group_display_name?: string
    actions?: { order_reverse: string }[]
  }
  sorOrder?: string
}

export type ActionSort = 'idx' | 'data_idx' | 'ip_ev'

export type RepItem = {
  id: string[]
  data_idx: number
  isEmpty?: boolean
  solution: Solution[]
  cssClass?: string
} & Record<ActionSort, number>

export type Res = {
  updated: boolean
  content: string
  actionsGrouping: string
  filterActions: FilterAction[]
  reportsStreetGrouping: string
  reportsStreetGroup: string
  flop_sortByIdx: string
  flop_sortByValue: string
  flop_sortByAction: FlopSortByAction
  flop_sortByTurn: string
  flop_sortBy: string
  flop_sortOrder: string
  flop_sortOrderDesc: boolean
  scrollerKey: string
  heroLeft: boolean
  sw_rep_strategy: string
  sw_rep_ev: string
  sw_rep_eq: string
  sw_rep_eqr: string
  colValue: ColValue
  heroRelPos: string
  chartSplit: boolean
  display_strategy: boolean
  oop_player: Player
  ip_player: Player
  display_oop: boolean
  display_ip: boolean
}

export interface ReportsLegacyStructureTurn {
  solutions: Solution[]
  player_solutions: {
    position: string
    is_hero: boolean
    relative_position: string
    solutions: {
      name: string
      ev: number
      eq: number
      eqr: number
    }[]
    total_ev: number
    total_eq: number
    total_eqr: number
  }[]
  totals: ActionSolution[]
  game_point: GamePoint
  results?: {
    game_point: GamePoint
    totals: ActionSolution[]
    filtered_totals?: any
    relative_hero_position?: string
    data?: any
  }
  filtered_totals?: any
  relative_hero_position?: string
  data?: any
}

export interface ReportsLegacyStructure {
  totals: any
  player_solutions?: any
  solutions?: any
  game_point: any
  results?: {
    game_point: GamePoint
    totals: ActionSolution[]
    filtered_totals?: any
    relative_hero_position?: string
    data?: any
    solved_action_counts?: any
  }
  filtered_totals?: any
  relative_hero_position?: string
  data?: any
  solved_action_counts?: any
}

export interface ReportsNewStructure {
  solutions: {
    name: string
    action_solutions: ActionSolution[]
    player_solutions: PlayerSolution[]
  }[]
  players: Player[]
  action_totals: ActionSolution[]
  player_totals: {
    position: string
    ev: number
    eq: number
    eqr: number
  }[]
  game_point: GamePoint
  results?: any
  data?: any
  filtered_action_totals?: ActionSolution[]
  filtered_player_totals?: PlayerSolution[]
}

export function isReportsLegacyStructure(
  data: ReportsLegacyStructure | AggregatedReportResponseSchema,
): data is ReportsLegacyStructure {
  return (
    ('player_solutions' in data &&
      'solutions' in data &&
      'totals' in data &&
      'game_point' in data) ||
    ('results' in data &&
      data.results !== undefined &&
      'filtered_totals' in data.results &&
      'relative_hero_position' in data.results &&
      'totals' in data.results &&
      'game_point' in data.results) ||
    ('data' in data &&
      Array.isArray(data.data) &&
      data.data.some(obj => Object.prototype.hasOwnProperty.call(obj, 'group')))
  )
}
